// CB:BEGIN fr-001-contractor-preferences-tab
import React, { useEffect } from 'react';
import { useCoreBidsSettings } from '../../hooks/useCoreBidsSettings';
import { SettingsSection } from '../settings/SettingsSection';
import { SettingsCheckbox } from '../settings/SettingsCheckbox';
import { SettingsSaveBar } from '../settings/SettingsSaveBar';
import { ServiceAreaSetup } from '../ServiceAreaSetup';

export default function CoreBidsContractorPreferencesTab() {
  const s = useCoreBidsSettings();
  // Sync contractor settings when direct contact is disabled - MUST be before early returns
  useEffect(() => {
    const settings = s.data?.contractorSettings;
    const userSettings = s.data?.userSettings;
    const directContactDisabled = !userSettings?.directContactEnabled;

    if (settings && directContactDisabled && (settings.allowCalls || settings.allowTexts || settings.preferredContactChannel !== 'IN_APP')) {
      s.updateLocal({
        contractorSettings: {
          ...settings,
          allowCalls: false,
          allowTexts: false,
          preferredContactChannel: 'IN_APP',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.data?.contractorSettings, s.data?.userSettings?.directContactEnabled]);

  // Stop Clerk modal from closing on clicks (bubble phase only, on tab root)
  const stopModalBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (s.loading) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
      </div>
    );
  }

  if (!s.data || !s.data.contractorSettings) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', color: '#dc2626' }}>
          {s.error || 'Contractor settings not available'}
        </div>
      </div>
    );
  }

  const settings = s.data.contractorSettings;
  const userSettings = s.data.userSettings;
  const directContactDisabled = !userSettings.directContactEnabled;

  const handleSave = async () => {
    try {
      await s.save({ contractorSettings: { ...settings } });
    } catch (err) {
      // Error is already set in hook
    }
  };

  return (
    <div onClick={stopModalBubble} className="corebids-settings" style={{ padding: '24px', maxWidth: '600px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a' }}>
        Contractor Preferences
      </h2>

      {s.error && !s.error.startsWith('✅') && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {s.error}
        </div>
      )}

      {directContactDisabled && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fffbeb', 
          color: '#92400e', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px',
          border: '1px solid #fde68a'
        }}>
          Direct contact is disabled in your Privacy settings. Calls and texts are not available.
        </div>
      )}

      <SettingsSection title="Service Area">
        <ServiceAreaSetup compact />
      </SettingsSection>

      <SettingsSection title="Quiet Hours">
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
          <label style={{ flex: 1 }}>
            <span style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: '#334155' }}>Start</span>
            <input
              type="time"
              value={settings.quietHoursStart || ''}
              onChange={(e) => s.updateLocal({
                contractorSettings: {
                  ...settings,
                  quietHoursStart: e.target.value || null,
                },
              })}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                color: '#1e293b',
                background: 'white'
              }}
            />
          </label>
          <label style={{ flex: 1 }}>
            <span style={{ display: 'block', marginBottom: '6px', fontSize: '14px', color: '#334155' }}>End</span>
            <input
              type="time"
              value={settings.quietHoursEnd || ''}
              onChange={(e) => s.updateLocal({
                contractorSettings: {
                  ...settings,
                  quietHoursEnd: e.target.value || null,
                },
              })}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                borderRadius: '6px', 
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                color: '#1e293b',
                background: 'white'
              }}
            />
          </label>
        </div>
      </SettingsSection>

      <SettingsSection title="Contact Preferences">
        <div className="mb-5">
          <SettingsCheckbox
            id="allow-calls"
            label="Allow phone calls"
            checked={settings.allowCalls}
            onCheckedChange={(checked) => s.updateLocal({
              contractorSettings: {
                ...settings,
                allowCalls: checked,
              },
            })}
            disabled={directContactDisabled}
            description={directContactDisabled ? "Disabled by Privacy settings" : undefined}
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="allow-texts"
            label="Allow text messages"
            checked={settings.allowTexts}
            onCheckedChange={(checked) => s.updateLocal({
              contractorSettings: {
                ...settings,
                allowTexts: checked,
              },
            })}
            disabled={directContactDisabled}
            description={directContactDisabled ? "Disabled by Privacy settings" : undefined}
          />
        </div>
        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>Preferred Contact Channel</span>
          <select
            value={settings.preferredContactChannel}
            onChange={(e) => s.updateLocal({
              contractorSettings: {
                ...settings,
                preferredContactChannel: e.target.value as any,
              },
            })}
            disabled={directContactDisabled}
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #cbd5e1',
              fontSize: '14px',
              color: '#1e293b',
              background: 'white',
              opacity: directContactDisabled ? 0.5 : 1
            }}
          >
            <option value="IN_APP">In-App Messaging</option>
            <option value="PHONE">Phone</option>
            <option value="SMS">SMS</option>
          </select>
        </label>
      </SettingsSection>

      <SettingsSection title="Additional Preferences">
        <div className="mb-5">
          <SettingsCheckbox
            id="auto-accept-messages"
            label="Auto-accept platform messages during business hours"
            description="Automatically accept messages from homeowners during your business hours."
            checked={settings.autoAcceptPlatformMessagesBusinessHours}
            onCheckedChange={(checked) => s.updateLocal({
              contractorSettings: {
                ...settings,
                autoAcceptPlatformMessagesBusinessHours: checked,
              },
            })}
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="pause-bidding"
            label="Pause Bidding"
            description="Temporarily stop receiving new project invitations."
            checked={settings.pauseBidding}
            onCheckedChange={(checked) => s.updateLocal({
              contractorSettings: {
                ...settings,
                pauseBidding: checked,
              },
            })}
            testId="contractor-pause-bidding"
          />
        </div>
        <div className="mt-4">
          <div className="font-medium mb-3 text-sm text-gray-900">Lead Types Allowed</div>
          <div className="mb-3">
            <SettingsCheckbox
              id="lead-insurance"
              label="Insurance claims"
              checked={settings.leadTypesAllowed?.insurance !== false}
              onCheckedChange={(checked) => s.updateLocal({
                contractorSettings: {
                  ...settings,
                  leadTypesAllowed: { ...settings.leadTypesAllowed, insurance: checked },
                },
              })}
            />
          </div>
          <div>
            <SettingsCheckbox
              id="lead-retail"
              label="Retail projects"
              checked={settings.leadTypesAllowed?.retail !== false}
              onCheckedChange={(checked) => s.updateLocal({
                contractorSettings: {
                  ...settings,
                  leadTypesAllowed: { ...settings.leadTypesAllowed, retail: checked },
                },
              })}
            />
          </div>
        </div>
      </SettingsSection>

      <SettingsSaveBar
        dirty={s.dirty}
        saving={s.saving}
        error={s.error}
        lastSavedAt={s.lastSavedAt}
        onSave={handleSave}
        onRevert={s.resetLocalToServer}
      />
    </div>
  );
}
// CB:END fr-001-contractor-preferences-tab
